import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

const InteractiveTour = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const tourSteps = [
    {
      title: 'Welcome to NexusCMS!',
      description: 'Let\'s take a quick tour of the platform. This interactive guide will show you the key features and how to use them.',
      target: null,
      position: 'center',
      route: '/dashboard'
    },
    {
      title: 'The Sidebar',
      description: 'This is your main navigation. Access all features from here including Pages, Newsletters, Media Library, and Settings.',
      target: '[data-testid="sidebar"]',
      position: 'right',
      route: '/dashboard'
    },
    {
      title: 'Dashboard Overview',
      description: 'Your dashboard shows key statistics and recent activity. Quick access to your pages, newsletters, and team.',
      target: null,
      position: 'center',
      route: '/dashboard'
    },
    {
      title: 'Create New Content',
      description: 'Use these buttons to quickly create new pages or newsletters. You can also access these from the sidebar.',
      target: null,
      position: 'center',
      route: '/dashboard'
    },
    {
      title: 'Pages Section',
      description: 'Here you can create and manage all your web pages. Use the block editor to build beautiful content.',
      target: null,
      position: 'center',
      route: '/pages'
    },
    {
      title: 'Newsletters',
      description: 'Create email newsletters, manage subscribers, and track analytics. Send beautiful emails to your audience.',
      target: null,
      position: 'center',
      route: '/newsletters'
    },
    {
      title: 'Media Library',
      description: 'Upload and manage all your images and files. Easily insert them into pages and newsletters.',
      target: null,
      position: 'center',
      route: '/media'
    },
    {
      title: 'Settings',
      description: 'Configure your account, branding, and preferences. Customize NexusCMS to match your needs.',
      target: null,
      position: 'center',
      route: '/settings'
    },
    {
      title: 'Security Settings',
      description: 'Enable Two-Factor Authentication and review audit logs to keep your account secure.',
      target: null,
      position: 'center',
      route: '/security-settings'
    },
    {
      title: 'Tour Complete!',
      description: 'You\'re all set! Explore the platform and start creating amazing content. Visit Help & Tutorials anytime for more guidance.',
      target: null,
      position: 'center',
      route: '/dashboard'
    }
  ];

  useEffect(() => {
    const step = tourSteps[currentStep];
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
    }

    // Highlight target element after navigation
    const timer = setTimeout(() => {
      if (step.target) {
        const element = document.querySelector(step.target);
        if (element) {
          setHighlightedElement(element);
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        setHighlightedElement(null);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [currentStep, navigate, location.pathname]);

  const nextStep = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const step = tourSteps[currentStep];
  const isLastStep = currentStep === tourSteps.length - 1;
  const isFirstStep = currentStep === 0;

  // Calculate tooltip position
  const getTooltipStyle = () => {
    if (step.position === 'center' || !highlightedElement) {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }

    const rect = highlightedElement.getBoundingClientRect();
    const padding = 20;

    switch (step.position) {
      case 'right':
        return {
          top: `${rect.top + rect.height / 2}px`,
          left: `${rect.right + padding}px`,
          transform: 'translateY(-50%)'
        };
      case 'bottom':
        return {
          top: `${rect.bottom + padding}px`,
          left: `${rect.left + rect.width / 2}px`,
          transform: 'translateX(-50%)'
        };
      case 'left':
        return {
          top: `${rect.top + rect.height / 2}px`,
          right: `${window.innerWidth - rect.left + padding}px`,
          transform: 'translateY(-50%)'
        };
      case 'top':
        return {
          bottom: `${window.innerHeight - rect.top + padding}px`,
          left: `${rect.left + rect.width / 2}px`,
          transform: 'translateX(-50%)'
        };
      default:
        return {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        };
    }
  };

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Highlight ring for target element */}
      {highlightedElement && (
        <div
          className="absolute border-4 border-indigo-500 rounded-lg pointer-events-none animate-pulse"
          style={{
            top: highlightedElement.getBoundingClientRect().top - 8,
            left: highlightedElement.getBoundingClientRect().left - 8,
            width: highlightedElement.getBoundingClientRect().width + 16,
            height: highlightedElement.getBoundingClientRect().height + 16,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)'
          }}
        />
      )}

      {/* Tour Tooltip */}
      <div
        className="absolute bg-white rounded-xl shadow-2xl p-6 max-w-md z-10"
        style={getTooltipStyle()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
        >
          <X size={20} />
        </button>

        {/* Progress indicator */}
        <div className="flex gap-1.5 mb-4">
          {tourSteps.map((_, index) => (
            <div
              key={index}
              className={`h-1.5 rounded-full transition-all ${
                index === currentStep
                  ? 'w-6 bg-indigo-600'
                  : index < currentStep
                  ? 'w-1.5 bg-indigo-400'
                  : 'w-1.5 bg-slate-200'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="mb-6">
          {isLastStep && (
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-emerald-600" size={32} />
            </div>
          )}
          <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {step.title}
          </h3>
          <p className="text-slate-600">{step.description}</p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <div className="text-sm text-slate-500">
            Step {currentStep + 1} of {tourSteps.length}
          </div>
          <div className="flex gap-2">
            {!isFirstStep && (
              <Button variant="outline" size="sm" onClick={prevStep}>
                <ChevronLeft size={16} className="mr-1" />
                Back
              </Button>
            )}
            <Button size="sm" onClick={nextStep}>
              {isLastStep ? (
                'Finish Tour'
              ) : (
                <>
                  Next
                  <ChevronRight size={16} className="ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveTour;
